import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Minus, TrendingDown, Info, Eye } from 'react-feather';
import { Popover, Modal } from 'antd';
import Select from 'react-select';
import axios from 'axios';

const TransportStatistics = () => {
    const [statistics, setStatistics] = useState({
        totalDeliveries: 0,
        totalRevenue: 0,
        totalRounds: 0,
        totalExpenses: 0,
        netProfit: 0,
        activeTransports: 0,
        averageDeliveryPrice: 0,
        loading: true
    });

    const [transports, setTransports] = useState([]);
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [selectedTransport, setSelectedTransport] = useState('all');
    const [clientOptions, setClientOptions] = useState([]);
    const [materialOptions, setMaterialOptions] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [expensesMatchFilters, setExpensesMatchFilters] = useState(true);
    const [downloading, setDownloading] = useState(false);

    // Daromad / Harajatlar kartasi ortidagi batafsil ro'yxat (ko'zcha bosilganda
    // ochiladigan modal). Redirect qilmaydi.
    const [detail, setDetail] = useState({
        open: false,
        type: null,       // 'deliveries' | 'expenses'
        title: '',
        loading: false,
        rows: [],
        totals: {},
    });

    useEffect(() => {
        fetchTransportStatistics();
    }, [selectedMonth, selectedTransport, selectedClient, selectedMaterial]);

    // Mijoz va material filtrlari uchun ro'yxatlar (bir marta yuklanadi)
    useEffect(() => {
        const fetchFilterOptions = async () => {
            try {
                const [clientsRes, materialsRes] = await Promise.all([
                    axios.get('/clients?is_all=true'),
                    axios.get('/materials?is_all=true'),
                ]);
                const toOptions = (res) => (res.data?.data || []).map(item => ({
                    value: item.id,
                    label: item.name,
                }));
                setClientOptions(toOptions(clientsRes));
                setMaterialOptions(toOptions(materialsRes));
            } catch (error) {
                console.error('Error fetching filter options:', error);
            }
        };

        fetchFilterOptions();
    }, []);

    const buildParams = () => {
        const params = {};

        // Add date filters
        if (selectedMonth !== 'all') {
            const [year, month] = selectedMonth.split('-').map(Number);
            const lastDay = new Date(year, month, 0).getDate();
            params.from_date = `${selectedMonth}-01`;
            params.to_date = `${selectedMonth}-${String(lastDay).padStart(2, '0')}`;
        }

        // Add transport filter
        if (selectedTransport !== 'all') {
            params.transport_id = selectedTransport;
        }

        if (selectedClient) {
            params.client_id = selectedClient.value;
        }

        if (selectedMaterial) {
            params.material_id = selectedMaterial.value;
        }

        return params;
    };

    const fetchTransportStatistics = async () => {
        try {
            setStatistics(prev => ({ ...prev, loading: true }));

            const response = await axios.get('/dashboard/transport-statistics', { params: buildParams() });
            const { data, transports: transportOptions, meta } = response.data;

            // Set transports for dropdown (only on first load)
            if (transports.length === 0) {
                setTransports(transportOptions || []);
            }

            setExpensesMatchFilters(meta?.expenses_match_filters !== false);

            setStatistics({
                totalDeliveries: data.total_deliveries,
                totalRevenue: data.total_revenue,
                totalRounds: data.total_rounds,
                totalExpenses: data.total_expenses,
                netProfit: data.net_profit,
                activeTransports: data.active_transports,
                averageDeliveryPrice: data.average_delivery_price,
                loading: false
            });
        } catch (error) {
            console.error('Error fetching transport statistics:', error);
            setStatistics(prev => ({ ...prev, loading: false }));
        }
    };

    const handleDownloadReport = async () => {
        try {
            setDownloading(true);
            const response = await axios.get('/dashboard/transport-statistics/export', { params: buildParams() });
            const link = response.data?.link;
            if (link) {
                window.open(link, '_blank');
            }
        } catch (error) {
            console.error('Error downloading transport statistics report:', error);
        } finally {
            setDownloading(false);
        }
    };

    const formatNumber = (num) => {
        const absNum = Math.abs(num);
        if (absNum >= 1000000) {
            return (num / 1000000).toFixed(1) + ' mln';
        } else if (absNum >= 1000) {
            return (num / 1000).toFixed(1) + ' ming';
        }
        return num.toString();
    };

    // To'liq raqam (bo'shliq bilan): 1 692 720
    const formatFull = (num) => Number(num || 0).toLocaleString('ru-RU').replace(/,/g, ' ');

    const openDetail = async (type, title) => {
        setDetail({ open: true, type, title, loading: true, rows: [], totals: {} });
        try {
            const response = await axios.get(`/dashboard/transport-statistics/${type}`, { params: buildParams() });
            const { data, ...totals } = response.data;
            setDetail(prev => ({ ...prev, loading: false, rows: data || [], totals }));
        } catch (error) {
            console.error('Error fetching transport detail:', error);
            setDetail(prev => ({ ...prev, loading: false }));
        }
    };

    const closeDetail = () => setDetail(prev => ({ ...prev, open: false }));

    const cards = [
        {
            title: 'Jami reys soni',
            value: statistics.totalRounds,
            icon: TrendingUp,
            color: 'info',
            suffix: 'ta',
            info: "Tanlangan davrdagi barcha reyslar yig'indisi"
        },
        {
            title: 'Jami daromad',
            value: formatNumber(statistics.totalRevenue),
            icon: DollarSign,
            color: 'success',
            suffix: ' so\'m',
            info: "Barcha reyslarning (reys soni * narxi) yig'indisi",
            detail: 'deliveries'
        },
        {
            title: 'Harajatlar',
            value: formatNumber(statistics.totalExpenses),
            icon: Minus,
            color: 'danger',
            suffix: ' so\'m',
            info: "Transport ehtiyot qismlari + Umumiy transport xarajatlari",
            detail: 'expenses'
        },
        {
            title: 'Sof foyda',
            value: formatNumber(statistics.netProfit),
            icon: statistics.netProfit >= 0 ? TrendingUp : TrendingDown,
            color: statistics.netProfit >= 0 ? 'success' : 'danger',
            suffix: ' so\'m',
            info: "Jami daromad - Harajatlar"
        }
    ];

    // Faqat birinchi yuklashda spinner ko'rsatiladi: aks holda filtr o'zgarganda
    // butun karta (filtrlar bilan birga) qayta chiziladi va tanlov yopilib qoladi.
    if (statistics.loading && transports.length === 0) {
        return (
            <div className="card">
                <div className="card-header">
                    <h5 className="card-title mb-0">Transport statistikasi</h5>
                </div>
                <div className="card-body">
                    <div className="d-flex justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="card-header">
                <div className="row align-items-center">
                    <div className="col">
                        <h5 className="card-title mb-0">Transport statistikasi</h5>
                    </div>
                    <div className="col-auto">
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            <div style={{width: '180px'}}>
                                <Select
                                    options={clientOptions}
                                    value={selectedClient}
                                    onChange={(option) => setSelectedClient(option || null)}
                                    placeholder={'Barcha mijozlar'}
                                    isClearable
                                />
                            </div>
                            <div style={{width: '180px'}}>
                                <Select
                                    options={materialOptions}
                                    value={selectedMaterial}
                                    onChange={(option) => setSelectedMaterial(option || null)}
                                    placeholder={'Barcha materiallar'}
                                    isClearable
                                />
                            </div>
                            <select
                                className="form-select"
                                value={selectedTransport}
                                onChange={(e) => setSelectedTransport(e.target.value)}
                                style={{width: '180px'}}
                            >
                                <option value="all">Barcha transportlar</option>
                                {transports.map(transport => (
                                    <option key={transport.id} value={transport.id}>
                                        {transport.label}
                                    </option>
                                ))}
                            </select>
                            <select 
                                className="form-select"
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                style={{width: '150px'}}
                            >
                                <option value="all">Hammasi</option>
                                {Array.from({ length: 12 }, (_, i) => {
                                    const date = new Date();
                                    date.setMonth(date.getMonth() - i);
                                    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                                    
                                    // Uzbek month names
                                    const uzbekMonths = [
                                        'Yanvar', 'Fevral', 'Mart', 'Aprel', 'May', 'Iyun',
                                        'Iyul', 'Avgust', 'Sentabr', 'Oktabr', 'Noyabr', 'Dekabr'
                                    ];
                                    
                                    const label = `${date.getFullYear()} ${uzbekMonths[date.getMonth()]}`;
                                    
                                    return (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    );
                                })}
                            </select>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleDownloadReport}
                                disabled={downloading}
                                style={{whiteSpace: 'nowrap'}}
                            >
                                {downloading ? 'Yuklanmoqda...' : 'Hisobot yuklash'}
                            </button>
                        </div>
                    </div>
                </div>
                {!expensesMatchFilters && (
                    <div className="text-muted mt-2" style={{fontSize: '12px'}}>
                        <Info size={13} className="me-1"/>
                        Xarajatlar va sof foyda faqat transport bo'yicha hisoblanadi — mijoz/material filtri ularga ta'sir qilmaydi.
                    </div>
                )}
            </div>
            <div className="card-body">
                <div className="row">
                    {cards.map((card, index) => (
                        <div key={index} className="col-12 col-sm-6 col-lg-3 mb-3">
                            <div className={`card border-${card.color} h-100`} style={{position: 'relative'}}>
                                <Popover content={card.info} title={card.title} trigger="click">
                                    <Info size={14} className="text-muted" style={{position: 'absolute', top: 8, right: 8, cursor: 'pointer'}} />
                                </Popover>
                                {card.detail && (
                                    <Eye
                                        size={15}
                                        className={`text-${card.color}`}
                                        title="Batafsil ko'rish"
                                        style={{position: 'absolute', top: 7, right: 28, cursor: 'pointer'}}
                                        onClick={() => openDetail(card.detail, card.title)}
                                    />
                                )}
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-shrink-0">
                                            <card.icon className={`feather text-${card.color}`} size={24} />
                                        </div>
                                        <div className="flex-grow-1 ms-3">
                                            <h6 className="card-title mb-0 text-muted">{card.title}</h6>
                                            <h4 className="mt-1 mb-0">
                                                {card.value}{card.suffix}
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Modal
                open={detail.open}
                title={detail.title}
                onCancel={closeDetail}
                footer={null}
                width={detail.type === 'expenses' ? 900 : 820}
            >
                {detail.loading ? (
                    <div className="d-flex justify-content-center py-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : detail.type === 'deliveries' ? (
                    <div className="table-responsive" style={{maxHeight: '60vh', overflowY: 'auto'}}>
                        <table className="table table-sm table-bordered mb-0">
                            <thead className="table-light" style={{position: 'sticky', top: 0}}>
                                <tr>
                                    <th>#</th>
                                    <th>Sana</th>
                                    <th>Mijoz</th>
                                    <th>Material</th>
                                    <th>Transport</th>
                                    <th>Haydovchi</th>
                                    <th className="text-end">Qatnov</th>
                                    <th className="text-end">Narxi</th>
                                    <th className="text-end">Jami</th>
                                </tr>
                            </thead>
                            <tbody>
                                {detail.rows.length === 0 && (
                                    <tr><td colSpan={9} className="text-center text-muted">Ma'lumot topilmadi</td></tr>
                                )}
                                {detail.rows.map((r, i) => (
                                    <tr key={i}>
                                        <td>{i + 1}</td>
                                        <td>{r.date}</td>
                                        <td>{r.client}</td>
                                        <td>{r.material}</td>
                                        <td>{r.transport}</td>
                                        <td>{r.driver}</td>
                                        <td className="text-end">{r.round}</td>
                                        <td className="text-end">{formatFull(r.price)}</td>
                                        <td className="text-end">{formatFull(r.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="table-light fw-bold">
                                    <td colSpan={6}>JAMI ({detail.totals.count || 0})</td>
                                    <td className="text-end">{detail.totals.total_rounds || 0}</td>
                                    <td></td>
                                    <td className="text-end">{formatFull(detail.totals.total_revenue)} so'm</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                ) : (
                    <div>
                        {!expensesMatchFilters && (
                            <div className="text-muted mb-2" style={{fontSize: '12px'}}>
                                <Info size={13} className="me-1"/>
                                Xarajatlar transport bo'yicha — mijoz/material filtri ta'sir qilmaydi.
                            </div>
                        )}
                        <div className="table-responsive" style={{maxHeight: '60vh', overflowY: 'auto'}}>
                            <table className="table table-sm table-bordered mb-0">
                                <thead className="table-light" style={{position: 'sticky', top: 0}}>
                                    <tr>
                                        <th>#</th>
                                        <th>Sana</th>
                                        <th>Turi</th>
                                        <th>Nomi</th>
                                        <th className="text-end">Miqdor</th>
                                        <th className="text-end">Narxi</th>
                                        <th className="text-end">Jami</th>
                                        <th>Sarflagan</th>
                                        <th>Izoh</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detail.rows.length === 0 && (
                                        <tr><td colSpan={9} className="text-center text-muted">Ma'lumot topilmadi</td></tr>
                                    )}
                                    {detail.rows.map((r, i) => (
                                        <tr key={i}>
                                            <td>{i + 1}</td>
                                            <td>{r.date}</td>
                                            <td>{r.type_label}</td>
                                            <td>{r.name}</td>
                                            <td className="text-end">{r.quantity}{r.unit ? ` ${r.unit}` : ''}</td>
                                            <td className="text-end">{formatFull(r.unit_price)}</td>
                                            <td className="text-end">{formatFull(r.total)}</td>
                                            <td>{r.consumed_by}</td>
                                            <td>{r.notes}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="table-light fw-bold">
                                        <td colSpan={6}>JAMI ({detail.totals.count || 0})</td>
                                        <td className="text-end">{formatFull(detail.totals.total_expenses)} so'm</td>
                                        <td colSpan={2}></td>
                                    </tr>
                                    {(detail.totals.total_general_expenses || 0) > 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-muted">shundan ehtiyot qism / umumiy</td>
                                            <td className="text-end text-muted">
                                                {formatFull(detail.totals.total_sparepart_expenses)} / {formatFull(detail.totals.total_general_expenses)}
                                            </td>
                                            <td colSpan={2}></td>
                                        </tr>
                                    )}
                                </tfoot>
                            </table>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default TransportStatistics;